import React from 'react';
import { Link } from 'react-router-dom';

const TermsPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-slate-900 to-gray-800 text-white">
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="mb-6">
          <Link to="/" className="text-blue-400 hover:text-blue-300 text-sm">
            ← Back to App
          </Link>
        </div>

        <h1 className="text-3xl font-bold mb-8">Terms of Service</h1>

        <div className="prose prose-invert max-w-none space-y-6">
          <p className="text-gray-300">
            <strong>Last Updated:</strong> November 22, 2025
          </p>

          <section>
            <h2 className="text-xl font-semibold mt-6 mb-3">1. Acceptance of Terms</h2>
            <p className="text-gray-300">
              By accessing or using backtime.live ("the Service"), you agree to be bound by these Terms of Service.
              If you do not agree to these terms, please do not use the Service.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mt-6 mb-3">2. Description of Service</h2>
            <p className="text-gray-300">
              backtime.live is a service timing application designed to help organizations manage and coordinate
              service schedules. The Service allows users to create, share, and view countdown timers for
              scheduled events.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mt-6 mb-3">3. User Accounts</h2>
            <p className="text-gray-300">
              To access certain features of the Service, you must create an account. You are responsible for:
            </p>
            <ul className="list-disc pl-6 text-gray-300 mt-2 space-y-1">
              <li>Maintaining the confidentiality of your account credentials</li>
              <li>All activities that occur under your account</li>
              <li>Notifying us immediately of any unauthorized use</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold mt-6 mb-3">4. Acceptable Use</h2>
            <p className="text-gray-300">
              You agree not to:
            </p>
            <ul className="list-disc pl-6 text-gray-300 mt-2 space-y-1">
              <li>Use the Service for any unlawful purpose</li>
              <li>Attempt to gain unauthorized access to the Service or its systems</li>
              <li>Interfere with or disrupt the Service or servers</li>
              <li>Upload malicious code or content</li>
              <li>Impersonate any person or entity</li>
              <li>Use the Service to spam or harass others</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold mt-6 mb-3">5. Intellectual Property</h2>
            <p className="text-gray-300">
              The Service and its original content, features, and functionality are owned by backtime.live
              and are protected by international copyright, trademark, and other intellectual property laws.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mt-6 mb-3">6. User Content</h2>
            <p className="text-gray-300">
              You retain ownership of any content you create using the Service (such as session names and
              schedules). By using the Service, you grant us a license to store, display, and transmit your
              content as necessary to provide the Service.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mt-6 mb-3">7. Public Sessions</h2>
            <p className="text-gray-300">
              If you make a session public, anyone with the link or QR code can view your session's schedule
              and timing information. You are responsible for ensuring you have the right to share this
              information publicly.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mt-6 mb-3">8. Service Availability</h2>
            <p className="text-gray-300">
              We strive to maintain high availability but do not guarantee uninterrupted access to the Service.
              We may modify, suspend, or discontinue the Service at any time without notice.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mt-6 mb-3">9. Disclaimer of Warranties</h2>
            <p className="text-gray-300">
              THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER
              EXPRESS OR IMPLIED. WE DO NOT WARRANT THAT THE SERVICE WILL BE ERROR-FREE OR UNINTERRUPTED.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mt-6 mb-3">10. Limitation of Liability</h2>
            <p className="text-gray-300">
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, BACKTIME.LIVE SHALL NOT BE LIABLE FOR ANY
              INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES ARISING FROM YOUR USE OF
              THE SERVICE.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mt-6 mb-3">11. Account Termination</h2>
            <p className="text-gray-300">
              We may terminate or suspend your account at any time for violations of these Terms. You may
              also delete your account at any time. Upon termination, your right to use the Service will
              immediately cease.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mt-6 mb-3">12. Changes to Terms</h2>
            <p className="text-gray-300">
              We reserve the right to modify these Terms at any time. We will notify users of significant
              changes. Your continued use of the Service after changes constitutes acceptance of the new Terms.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mt-6 mb-3">13. Governing Law</h2>
            <p className="text-gray-300">
              These Terms shall be governed by the laws of the United States, without regard to its conflict
              of law provisions.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mt-6 mb-3">14. Contact Us</h2>
            <p className="text-gray-300">
              If you have any questions about these Terms, please contact us at support@backtime.live.
            </p>
          </section>
        </div>

        <div className="mt-12 pt-6 border-t border-gray-700 text-center text-sm text-gray-500">
          <Link to="/privacy" className="hover:text-gray-300 mr-4">Privacy Policy</Link>
          <Link to="/about" className="hover:text-gray-300">About</Link>
        </div>
      </div>
    </div>
  );
};

export default TermsPage;
